/**
 * Classifica por que um workspace não pode ser consultado no console (plataforma ou parceiro).
 * Prioridade: sem permissão > excluído > não encontrado > falha de rede/servidor.
 */

const PERMISSION_MESSAGE_RE = /sem permissão|no permission|access denied|acesso restrito/i;
const NOT_FOUND_CODES = new Set(["dominio_not_found", "workspace_not_found", "partner_workspace_not_found"]);

export function parseWorkspaceApiError(err) {
  const status = err?.response?.status ?? err?.status ?? null;
  const body = err?.response?.data ?? {};
  const message =
    (typeof body?.message === "string" && body.message) ||
    (Array.isArray(body?.message) && body.message[0]) ||
    (typeof err?.message === "string" && err.message) ||
    "";
  const code =
    (typeof body?.code === "string" && body.code) ||
    (typeof body?.data?.code === "string" && body.data.code) ||
    (typeof err?.code === "string" && err.code) ||
    null;
  return { status, message, code };
}

/** 403s de auth/sessão que não são “falta de permissões_dominios” do workspace da URL. */
const AUTH_SESSION_DENIED_CODES = new Set([
  "errors.auth.platform_no_tenant_user",
  "errors.auth.platform_workspace_unknown",
  "errors.auth.partner_no_tenant_user",
  "errors.auth.session_revoked",
  "errors.auth.user_inactive",
  "errors.auth.company_mismatch",
  "billing_admin_partner_forbidden",
  "platform_console_section_denied",
  "platform_console_feature_disabled",
]);

function isPermissionFailure({ status, message, code }) {
  if (code === "workspace_access_denied") return true;
  if (AUTH_SESSION_DENIED_CODES.has(code)) return false;
  if (status === 403 && PERMISSION_MESSAGE_RE.test(message)) return true;
  // Sem code conhecido: 403 genérico ainda trata como sem permissão (legado).
  if (status === 403 && !code && !NOT_FOUND_CODES.has(code)) return true;
  if (status === 403 && code && !NOT_FOUND_CODES.has(code) && !String(code).startsWith("errors.")) {
    return true;
  }
  return false;
}

function isNotFoundFailure({ status, message, code }) {
  if (NOT_FOUND_CODES.has(code)) return true;
  if (status === 404) return true;
  if (/não encontrado|not found|no encontrado/i.test(message)) return true;
  return false;
}

export function isWorkspaceExcluded(dominioRow, detail) {
  if (detail?.availability?.status === "excluded") return true;
  if (detail?.availability?.code === "workspace_excluded") return true;
  if (dominioRow == null) return false;
  return Number(dominioRow.excluido) === 1;
}

/**
 * @returns {null | { kind: string, status?: number, code?: string, message?: string, host?: string, dominioId?: string }}
 */
export function resolveWorkspaceUnavailableState({ loadFailure, detail, dominioId }) {
  const id = String(dominioId || "").trim();
  if (!id) {
    return { kind: "invalid_id", dominioId: id };
  }

  if (loadFailure) {
    const host = detail?.dominio?.dominio || "";
    if (isPermissionFailure(loadFailure)) {
      return { kind: "no_permission", ...loadFailure, host, dominioId: id };
    }
    if (isNotFoundFailure(loadFailure)) {
      return { kind: "not_found", ...loadFailure, host, dominioId: id };
    }
    return { kind: "load_error", ...loadFailure, host, dominioId: id };
  }

  const dominioRow = detail?.dominio;
  if (isWorkspaceExcluded(dominioRow, detail)) {
    return {
      kind: "excluded",
      host: dominioRow?.dominio || "",
      dominioId: id,
      code: "workspace_excluded",
    };
  }

  return null;
}

/** Estados que impedem ver abas e dados operacionais (inclui excluído — só leitura de cadastro). */
export function isWorkspaceAccessBlocked(unavailable) {
  if (!unavailable) return false;
  return ["no_permission", "not_found", "invalid_id", "load_error", "excluded"].includes(unavailable.kind);
}
